import React, { useState, useRef, useEffect } from "react";
import { askQuestioReportReader } from "../../../utilities/EmgptUtilities";
import { fetchFollowUpSuggestions } from "../../../utilities/DocAssistantUtilities";
import { normalizeChatSuggestion } from "../../../utilities/chatSuggestionUtilities";
import ChatOverlayFrame from "../../Common/ChatOverlayFrame";

const EmGptChatBox = ({
  inUseDocumentIds,
  height,
  maxWidth,
  getPageNumber,
  setPageNumbers,
  chatSuggestions,
  document_type,
  selectedFilters,
}) => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [documentIdInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const chatSurfaceVars = {
    "--ea-chat-page-bg": "#FFFFFF",
    "--ea-chat-surface": "#FFFFFF",
    "--ea-chat-surface-alt": "#F7FBFF",
    "--ea-chat-border": "#E9ECEF",
    "--ea-chat-text": "#0B1220",
    "--ea-chat-muted": "#5B6472",
    "--ea-chat-link": "#0D6EFD",
    "--ea-chat-accent": "#0D6EFD",
    "--ea-chat-accent-soft": "rgba(13, 110, 253, 0.12)",
    "--ea-chat-question-bg": "linear-gradient(135deg, #0D6EFD 0%, #175CD3 100%)",
    "--ea-chat-question-text": "#FFFFFF",
    "--ea-chat-answer-bg": "rgba(255, 255, 255, 0.94)",
    "--ea-chat-answer-text": "#0B1220",
    "--ea-chat-body-bg": "linear-gradient(180deg, #F7FBFF 0%, #FFFFFF 52%)",
    "--ea-chat-composer-bg": "linear-gradient(180deg, #FBFDFF 0%, #F5F8FC 100%)",
    "--ea-chat-input-bg": "#FFFFFF",
    "--ea-chat-chip-bg": "#F5F9FF",
    "--ea-chat-ghost-bg": "rgba(255, 255, 255, 0.88)",
    "--ea-chat-toast-bg": "#0B1220",
    "--ea-chat-shadow": "0 22px 60px rgba(16, 24, 40, 0.10)",
  };

  const messagesEndRef = useRef();

  useEffect(() => {

    setDynamicSuggestions([]);
    if (messages[messages.length -1]?.type === "answer") {
      if (document_type === "IMF") {
        fetchFollowUpSuggestions(
          messages[messages.length -2].text,
          messages[messages.length -1].text,
          setDynamicSuggestions,
          "report_reader_IMF",
          [selectedFilters.country]
        );
      } else if (document_type === "Company") {
        const companyName = selectedFilters?.selected_company || "";
        const dataSource = selectedFilters?.data_source || [""];

        fetchFollowUpSuggestions(
          messages[messages.length -2].text,
          messages[messages.length -1].text,
          setDynamicSuggestions,
          "report_reader_COMPANY",
          [companyName, dataSource[0] || ""]
        );
      }
    }
  }, [messages]);


  useEffect(() => {
    const messagesContainer = messagesEndRef.current;
    if (messagesContainer) {
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages,dynamicSuggestions]);

  const handleInputChange = (e) => setUserInput(e.target.value);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      setShowSuggestions(false);
      askQuestioReportReader(
        setIsLoading,
        userInput,
        setUserInput,
        documentIdInput,
        inUseDocumentIds,
        setMessages,
        messages,
        setPageNumbers,
        getPageNumber
      );
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const normalizedSuggestion = normalizeChatSuggestion(suggestion);
    if (!normalizedSuggestion) return;

    setUserInput(normalizedSuggestion.question);
    setShowSuggestions(false);
    askQuestioReportReader(
      setIsLoading,
      normalizedSuggestion.question,
      setUserInput,
      documentIdInput,
      inUseDocumentIds,
      setMessages,
      messages,
      setPageNumbers,
      getPageNumber,
      normalizedSuggestion.evidence_refs
    );
  };

  useEffect(() => {
    if (
      inUseDocumentIds &&
      inUseDocumentIds.length === 1 &&
      inUseDocumentIds[0]["document_name"] &&
      messages.length > 1
    ) {
      setMessages((prev) => [
        ...prev,
        {
          type: "info",
          text: `Selected Document : ${inUseDocumentIds[0]["document_name"]}`,
        },
      ]);
    }
  }, [inUseDocumentIds]);

  return (
    <ChatOverlayFrame
      isOpen={isOverlayOpen}
      onClose={() => setIsOverlayOpen(false)}
    >
      <div className="container-fluid h-100 w-100 p-0">
        <div
          className="card h-100 w-100 ea-chat-surface ea-chat-shell"
          style={chatSurfaceVars}
        >
        <div className="ea-chat-header d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="ea-chat-title-group">
            <p className="ea-chat-title">Report Reader Q&amp;A</p>
            <p className="ea-chat-subtitle">
              Ask precise follow-ups on the selected report set and jump back to cited pages.
            </p>
          </div>
          <div className="ea-chat-header-actions">
            <button
              type="button"
              className="btn btn-sm ea-chat-ghost-btn"
              onClick={() => setIsOverlayOpen((prev) => !prev)}
              title={isOverlayOpen ? "Exit full screen" : "Open full screen"}
              aria-pressed={isOverlayOpen}
            >
              <i
                className={`fa-solid ${
                  isOverlayOpen ? "fa-compress" : "fa-expand"
                } me-2`}
              />
              {isOverlayOpen ? "Exit" : "Expand"}
            </button>
          </div>
        </div>
        <div className="card-body d-flex flex-column h-100 p-0">
          <div
            className="messages mb-3 overflow-auto flex-grow-1 ea-chat-scroll p-3"
            ref={messagesEndRef}
          >
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`alert ea-chat-bubble ${
                  msg.type === "question"
                    ? "text-right ea-chat-bubble--question"
                    : msg.type === "info"
                    ? "text-center ea-chat-bubble--info"
                    : "text-left ea-chat-bubble--answer"
                }`}
                style={{
                  maxWidth: maxWidth ? maxWidth : "80%",
                  margin:
                    msg.type === "question" ? "0 0 10px auto" : "0 auto 10px 0",
                }}
              >
                <div className="ea-chat-rich">{msg.text}</div>
              </div>
            ))}

            {showSuggestions && chatSuggestions.length > 0 && (
              <div className="mb-3 px-2 py-1 rounded">
                <div className="row">
                  {chatSuggestions.map((rawSuggestion, index) => {
                    const suggestion = normalizeChatSuggestion(rawSuggestion);
                    if (!suggestion) return null;

                    return (
                      <div
                        className="col-6 mb-2"
                        key={`${suggestion.question}-${index}`}
                      >
                        <button
                          className="btn btn-outline-secondary w-100 ea-chat-suggestion-btn"
                          onClick={() => handleSuggestionClick(suggestion)}
                          style={{ fontSize: "14px" }}
                        >
                          {suggestion.question}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {dynamicSuggestions?.length > 0 && (
              <div className="mb-3 px-2 py-1 rounded">
                <div className="row">
                  {dynamicSuggestions.map((rawSuggestion, index) => {
                    const suggestion = normalizeChatSuggestion(rawSuggestion);
                    if (!suggestion) return null;

                    return (
                      <div
                        className="col-6 mb-2"
                        key={`${suggestion.question}-${index}`}
                      >
                        <button
                          className="btn btn-outline-secondary w-100 ea-chat-suggestion-btn"
                          onClick={() => handleSuggestionClick(suggestion)}
                          style={{ fontSize: "14px" }}
                        >
                          {suggestion.question}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isLoading && (
              <div
                className="d-flex justify-content-center align-items-center my-2 text-left ea-chat-loading-bubble"
                style={{
                  margin: "0 auto 10px 0",
                }}
              >
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
              </div>
            )}
          </div>

          <div className="input-group mt-auto ea-chat-composer ea-chat-composer-row">
            <input
              type="text"
              className="form-control ea-chat-input"
              placeholder="Type your question"
              value={userInput}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
            <button
              className="btn btn-primary ea-chat-send-btn"
              onClick={() => {
                setShowSuggestions(false);
                askQuestioReportReader(
                  setIsLoading,
                  userInput,
                  setUserInput,
                  documentIdInput,
                  inUseDocumentIds,
                  setMessages,
                  messages,
                  setPageNumbers,
                  getPageNumber
                );
              }}
              disabled={isLoading}
            >
              Send
            </button>
          </div>
        </div>
        </div>
      </div>
    </ChatOverlayFrame>
  );
};

export default EmGptChatBox;
